import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ReportColumnPreferenceService, UserReportColumnsPreferences } from '../../services/utils/report-column-preference.service';
import { LoaderService } from '../../services/loader/loader.service';
import { GlobalExportProgressService } from '../../services/export/global-export-progress.service';
import { ExcelReportService } from '../../services/utils/excel-report.service';
import { GlobalService } from '../../services/global/global-service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { FieldType } from '../../enums/template';
import { LocalStorageService } from '../../services/local-storage/localstorage.service';
import { UserPreferencesData } from '../../models/user-preferences.model';

interface ExportColumn {
  field: string;
  headerText: string;
  selected: boolean;
  type?: 'static' | 'dynamic';
  isCheckBox?: boolean;
  isAction?: boolean;
}

interface SavedColumnPreference {
  field: string;
  selected?: boolean;
}

interface ExportJsonItem {
  itemCode: string;
  caption?: string;
  fieldType?: string;
  dataSource?: string;
  ans?: unknown;
}

interface ExportJsonSection {
  sectionNo?: number;
  items?: ExportJsonItem[];
}

interface ExportJsonPage {
  sections?: ExportJsonSection[];
}

interface ExportDataRow {
  jsonData?: string | ExportJsonPage[];
  [key: string]: unknown;
}

interface LookupOption {
  id?: unknown;
  name?: string;
  woCode?: string;
  assetId?: string;
}

@Component({
  selector: 'app-export-data-dialog',
  templateUrl: './export-data-dialog.component.html',
  styleUrls: ['./export-data-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-existing NgModule-declared component; converting to standalone is a separate, larger migration (COL-37), out of scope here
  standalone: false
})
export class ExportDataDialogComponent implements OnInit {
  data = inject(MAT_DIALOG_DATA);
  private reportColumnPrefService = inject(ReportColumnPreferenceService);
  private loaderService = inject(LoaderService);
  private exportProgress = inject(GlobalExportProgressService);
  private excelService = inject(ExcelReportService);
  private globalService = inject(GlobalService);
  private localStorageService = inject(LocalStorageService);
  private dialogRef = inject<MatDialogRef<ExportDataDialogComponent>>(MatDialogRef);

  searchTerm = '';
  allColumns: ExportColumn[] = [];
  allSelected = false;
  filteredSelectedColumns: ExportColumn[] = [];
  filteredUnselectedColumns: ExportColumn[] = [];
  help = {
    helpColumnText: `This panel lets you configure which columns appear in your report and how they're ordered.
    Use the search box to quickly find columns, drag and drop to rearrange them, and check or uncheck columns to include or exclude them.
    You can also save your preferences to keep the same layout for future downloads.`
  };
  hasSelectedColumns = false;
  hasChanges = false;
  private initialColumnPreferences = '';

  constructor() {
    const data = this.data;

    const { dynamicColumns } = data;
    const staticCols = this.getStaticColumnList();

    this.allColumns = [
      ...staticCols.map(c => ({ ...c, type: 'static', selected: true })),
      ...(dynamicColumns?.length
        ? dynamicColumns.map(c => ({ ...c, type: 'dynamic', selected: true }))
        : [])
    ];

    this.allSelected = true;
    this.updateFilteredLists();
    this.updateButtonVisibility();
  }

  async ngOnInit() {
    const userId = this.data.userId;          // passed from parent
    const reportName = this.data.reportName;  // passed from parent
    if (!userId || !reportName) return;

    const payload = { userId, reportName };
    this.reportColumnPrefService.getPreferences(payload).subscribe({
      next: (res) => {
        if (res.length == 1 && res[0].columnPreferences) {
          const savedCols = JSON.parse(res[0].columnPreferences);

          // New format stores all columns with a `selected` flag.
          // Old format stored only selected columns (no flag).
          const hasSelectedFlag = savedCols.length > 0 && 'selected' in savedCols[0];

          if (hasSelectedFlag) {
            const savedMap = new Map(savedCols.map((s: SavedColumnPreference) => [s.field, s.selected ?? true]));
            // Columns not in saved prefs are new — default to selected
            this.allColumns.forEach(col => {
              col.selected = savedMap.has(col.field) ? !!savedMap.get(col.field) : true;
            });

            const savedOrderMap = new Map(savedCols.map((s: SavedColumnPreference, i: number) => [s.field, i]));
            const selected = this.allColumns
              .filter(c => c.selected)
              .sort((a, b) => {
                const ai: number = savedOrderMap.has(a.field) ? (savedOrderMap.get(a.field) as number) : Number.MAX_SAFE_INTEGER;
                const bi: number = savedOrderMap.has(b.field) ? (savedOrderMap.get(b.field) as number) : Number.MAX_SAFE_INTEGER;
                return ai - bi;
              });
            const unselected = this.allColumns.filter(c => !c.selected);
            this.allColumns = [...selected, ...unselected];
          } else {
            // Legacy format only lists previously-selected columns, so it can't tell
            // "existed but was deliberately unchecked" apart from "didn't exist yet".
            // A cf_<fieldKey> custom-field column, or a hierarchyXxx COL-817 column, can
            // only be the latter (neither existed before this saved preference could
            // have), so those default to selected even under a legacy save; other
            // unknown static columns keep the original deselect-by-omission behaviour.
            const savedFields = new Set(savedCols.map((s: SavedColumnPreference) => s.field));
            this.allColumns.forEach(col => {
              col.selected = savedFields.has(col.field) || col.field.startsWith('cf_') || col.field.startsWith('hierarchy');
            });
          }

          // Snapshot the restored state (with saved order) as the baseline for hasChanges —
          // so the "Save & Download" button only appears when the user actually modifies something.
          this.initialColumnPreferences = JSON.stringify(
            this.allColumns.filter(c => c.selected).map(c => ({ field: c.field, headerText: c.headerText }))
          );

          this.updateFilteredLists();
        }
      },
      error: (err) => console.error('Error loading column preferences', err)
    });
  }

  getStaticColumnList() {
    const filteredCols = this.data.staticColumns.filter(
      (col) => col.field && !col.isCheckBox && !col.isAction
    );

    // 🧩 Step 2: Normalize field names for time columns
    // → adds "_time" suffix but keeps unique identity
    const normalizedCols = filteredCols.map((col) => {
      // A time column whose field already is its own (e.g. Boots On Ground's createdDate_time) stays.
      if (col.type === 'time' && !col.field.endsWith('_time')) {
        return { ...col, field: `${col.field}_time` };
      }
      return col;
    });

    // 🧩 Step 3: Deduplicate by BOTH headerText and field
    const staticCols = normalizedCols.filter(
      (col, index, self) =>
        index ===
        self.findIndex(
          (c) =>
            c.headerText === col.headerText &&
            c.field === col.field
        )
    );

    return staticCols;
  }

  /** 🔍 Search filter logic */
  onSearchChange() {
    this.updateFilteredLists();
  }

  /** 🧮 Update filtered column lists */
  private updateFilteredLists() {
    const term = this.searchTerm.toLowerCase();
    const filtered = this.allColumns.filter(c =>
      c.headerText.toLowerCase().includes(term)
    );
    this.filteredSelectedColumns = filtered.filter(c => c.selected);
    this.filteredUnselectedColumns = filtered.filter(c => !c.selected);
    this.updateButtonVisibility();
  }
  private updateButtonVisibility() {
    // 1️⃣ Check if any column is selected
    this.hasSelectedColumns = this.allColumns.some(c => c.selected);

    // 2️⃣ Compare current selection/order to the initially saved one
    const currentPref = JSON.stringify(
      this.allColumns
        .filter(c => c.selected)
        .map(c => ({ field: c.field, headerText: c.headerText }))
    );

    this.hasChanges = this.initialColumnPreferences
      ? currentPref !== this.initialColumnPreferences
      : this.hasSelectedColumns; // if no saved prefs yet, treat any selection as change
  }
  /** ✅ Select All toggle */
  selectAll() {
    this.allSelected = true;
    this.allColumns.forEach(c => (c.selected = this.allSelected));
    this.updateFilteredLists();
  }
  clearAll() {
    this.allSelected = false;
    this.allColumns.forEach(c => (c.selected = this.allSelected));
    this.updateFilteredLists();
  }
  /** 🟦 Individual column toggle */
  onToggleColumn(event: MatCheckboxChange, col: ExportColumn) {
    col.selected = event.checked;
    this.allSelected = this.allColumns.every(c => c.selected);
    this.updateFilteredLists();
  }

  /** ↕️ Drag & drop ordering */
  drop(event: CdkDragDrop<ExportColumn[]>) {
    const movedItem = this.filteredSelectedColumns[event.previousIndex];
    const prevIndexInAll = this.allColumns.indexOf(movedItem);

    const targetItem = this.filteredSelectedColumns[event.currentIndex];
    const newIndexInAll = this.allColumns.indexOf(targetItem);

    moveItemInArray(this.allColumns, prevIndexInAll, newIndexInAll);
    this.updateFilteredLists();
  }

  /** ❌ Cancel */
  onCancel() {
    this.dialogRef.close();
  }

  private savePreferences() {
    const userId = this.data.userId;
    const reportName = this.data.reportName;

    // Every column (selected or not) is stored with an explicit `selected` flag, so a future
    // restore can tell "not selected because it's new" apart from "not selected because the
    // user hid it" — a column absent from this list entirely is new since last save and
    // defaults to visible (see the restore logic in ngOnInit above).
    const requestPayload: UserReportColumnsPreferences = {
      userId,
      reportName,
      columnPreferences: JSON.stringify(this.allColumns.map(c => ({
        field: c.field,
        headerText: c.headerText,
        selected: c.selected
      })))
    };

    this.reportColumnPrefService.savePreferences(requestPayload).subscribe({
      // eslint-disable-next-line @typescript-eslint/no-empty-function -- fire-and-forget save, no follow-up needed on success
      next: () => { },
      error: (err) => console.error('❌ Error saving preferences:', err)

    });
  }
  /** 📥 Download / Confirm */
  onDownload(saveColPref: boolean) {

    const selectedOrdered = this.allColumns.filter(c => c.selected);
    if (saveColPref) {
      this.savePreferences();
    }

    if (Array.isArray(selectedOrdered) && selectedOrdered.length > 0) {
      const selectedColumns = selectedOrdered;
      const columnNames = selectedColumns.map(c => c.field);
      this.generateExcel(columnNames);
    } else {
      console.warn('No columns selected.');
    }

    this.dialogRef.close(selectedOrdered);
  }

  async generateExcel(staticCols: string[] = []) {
    const feedbackJobId = this.exportProgress.startClientDownload(this.data.exportFileName ?? "Report.xlsx", "excel");
    try {
      // Step 1: Build headers with friendly names
      this.loaderService.showLoader('Preparing export...');
      const headers = this.getColumnHeaders(staticCols);

      // Step 2: Fetch logo and export Excel
      const companyLogoUrl = (await lastValueFrom(this.globalService.getCompanyProfile(this.data.companyId)))?.[0]?.companyLogoURL;
      const logoBase64 = companyLogoUrl?.length ? await this.fetchImageAsBase64(companyLogoUrl) : '';

      // Step 3: Fetch data for export
      this.loaderService.showLoader('Fetching data...');
      const exportData = await this.data.getExportData();

      // Step 4: Enrich dynamic data
      this.loaderService.showLoader('Enriching data...');
      const enrichedData = this.data.exportAllJson ? await this.getDynamicData(exportData) : exportData;

      // Step 5: Export data to excel
      this.loaderService.showLoader('Exporting data...');
      // The sheet's title: reportTitle when the saved-columns key (reportName) isn't the title too.
      const title = this.data.reportTitle ?? this.data.reportName;
      if (this.data.groupByInfo) {
        await this.excelService.generateMultiSheetReport(
          logoBase64, title, headers, enrichedData, this.data.exportFileName, this.data.groupByInfo
        );
      } else {
        await this.excelService.generateReport(
          logoBase64, title, headers, enrichedData, this.data.exportFileName
        );
      }

      this.loaderService.hideLoader();
      this.exportProgress.finishClientDownload(feedbackJobId, true);
    } catch (err) {
      // The header pill tells the user; the console keeps the cause, which the pill can't say.
      console.error('Excel export failed:', err);
      this.loaderService.hideLoader();
      this.exportProgress.finishClientDownload(feedbackJobId, false, "Could not generate the Excel report.");
    }
  }

  async getDynamicData(exportData: ExportDataRow[]){
    // Loop through rows to enrich with local dates and jsonData
    const replaceChar = /\./gi;
    const enrichedRows: ExportDataRow[] = [];
    // null = the caller can't read that list (e.g. a SubC without access to that module).
    const lookUpDataset: Record<string, LookupOption[] | null> = {};
    const userPreferencesData: UserPreferencesData | null = this.localStorageService.getItem('userPreferences') || {};

    for (const row of exportData) {
      if (typeof row.jsonData === 'string') {
        row.jsonData = JSON.parse(row.jsonData);
      }

      if (!row.jsonData || typeof row.jsonData !== 'object') {
        // enrichedRows.push({ ...row });
        continue;
      }

      const newRow: ExportDataRow = { ...row };

      // Merge jsonData JSONB values into the row (headers already added above)
      for (const page of row.jsonData) {
        for (const section of page.sections ?? []) {
          for (const item of section.items ?? []) {
            const formattedItemCode = item.itemCode.replace(replaceChar, "") + (section.sectionNo ?? '');
            if (item.fieldType === FieldType.LookUp) {
              if (item.dataSource === 'State' || item.dataSource === 'Supervisor') {
                newRow[formattedItemCode] = item.ans;
              }
              else{
                if (item.dataSource && !(item.dataSource in lookUpDataset)) {
                  try {
                    lookUpDataset[item.dataSource] = (await firstValueFrom(this.globalService.getByModule(
                      item.dataSource, userPreferencesData?.selectedCompanyId, userPreferencesData?.userId, this.localStorageService.getItem('partnerCompanyId') || 0, false)
                    )) as unknown as LookupOption[];
                  } catch {
                    // One unreadable lookup list must not fail the whole export: fetch it once, then
                    // export that question's raw answer.
                    lookUpDataset[item.dataSource] = null;
                  }
                }

                const lookUpData = item.dataSource ? lookUpDataset[item.dataSource] : undefined;
                if (lookUpData === null) {
                  newRow[formattedItemCode] = item.ans;
                }
                else if(item.dataSource === 'Work Order'){
                  const obj = lookUpData?.find(x => x.id == item.ans);
                  newRow[formattedItemCode] = obj
                    ? `${obj.woCode ?? ''} (AssetId: ${obj.assetId ?? ''})`
                    : (item.ans ?? '');
                }
                else {
                  newRow[formattedItemCode] = lookUpData?.find(x => x.id == item.ans)?.name;
                }
              }
            }
            else if (!item.fieldType || ![FieldType.Image, FieldType.Signature].includes(item.fieldType as FieldType)) {
              newRow[formattedItemCode] = item.ans;
            }
          }
        }
      }
      enrichedRows.push(newRow);
    }

    return enrichedRows;
  }

  getColumnHeaders(staticCols: string[] = []) {
    const headers = staticCols.map(field => {
      const baseField = field.replace('_time', '');
      const colDef = this.data.staticColumns.find(c => c.field === baseField);
      let headerText = colDef?.headerText || field;

      if (field.endsWith('_time')) {
        if (headerText.toLowerCase().includes('date')) {
          headerText = headerText.replace(/date/i, 'Time'); // e.g. "Submitted Date" → "Submitted Time"
        } else {
          headerText = `${headerText} Time`;
        }
      }

      return { header: headerText, key: field, sectionCode: '' };
    });

    return headers;
  }

  async fetchImageAsBase64(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string); // this is a data URL (base64)
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
